import { VARIANT_ACCENT_OKLCH, type VariantType } from "@upsquad/shared";
import { createContext, type ReactNode, useContext, useMemo, useState } from "react";

export type CommunityVariant = VariantType;

export interface VariantPalette {
	accent: string;
	accentSoft: string;
	accentSoftHover: string;
	accentInk: string;
	accentRing: string;
	label: string;
}

const FOREGROUND = "oklch(0.2103 0.0059 285.89)";

const VARIANT_LABELS: Record<CommunityVariant, string> = {
	default: "Blue",
	coral: "Coral",
	green: "Green",
	sky: "Sky",
};

function buildPalette(variant: CommunityVariant): VariantPalette {
	const accent = VARIANT_ACCENT_OKLCH[variant].light;
	return {
		accent,
		accentSoft: `color-mix(in oklab, ${accent} 15%, transparent)`,
		accentSoftHover: `color-mix(in oklab, ${accent} 20%, transparent)`,
		accentInk: `color-mix(in oklab, ${accent} 70%, ${FOREGROUND} 30%)`,
		accentRing: `color-mix(in oklab, ${accent} 30%, transparent)`,
		label: VARIANT_LABELS[variant],
	};
}

const isCommunityVariant = (value: string): value is CommunityVariant => value in VARIANT_ACCENT_OKLCH;

export const COMMUNITY_VARIANTS: ReadonlyArray<CommunityVariant> = Object.keys(VARIANT_ACCENT_OKLCH).filter(isCommunityVariant);

const PALETTES: Record<CommunityVariant, VariantPalette> = {
	default: buildPalette("default"),
	coral: buildPalette("coral"),
	green: buildPalette("green"),
	sky: buildPalette("sky"),
};

interface ContextValue {
	variant: CommunityVariant;
	setVariant: (next: CommunityVariant) => void;
	palette: VariantPalette;
}

const Ctx = createContext<ContextValue | null>(null);

interface ProviderProps {
	children: ReactNode;
	initial?: CommunityVariant;
}

export function CommunityVariantProvider({ children, initial = "default" }: ProviderProps) {
	const [variant, setVariant] = useState<CommunityVariant>(initial);
	const value = useMemo<ContextValue>(() => ({ variant, setVariant, palette: PALETTES[variant] }), [variant]);
	return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCommunityVariant(): ContextValue {
	const ctx = useContext(Ctx);
	if (!ctx) {
		return { variant: "default", setVariant: () => undefined, palette: PALETTES.default };
	}
	return ctx;
}

export function getVariantPalette(variant: CommunityVariant): VariantPalette {
	return PALETTES[variant];
}
